import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, tokens, type AdminDto } from '../lib/api';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: Status;
  admin: AdminDto | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [admin, setAdmin] = useState<AdminDto | null>(null);

  const refreshMe = useCallback(async () => {
    try {
      if (!tokens.access && !tokens.refresh) {
        setStatus('anonymous');
        return;
      }
      const { admin } = await api.me();
      setAdmin(admin);
      setStatus('authenticated');
    } catch {
      tokens.clear();
      setAdmin(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (username: string, password: string) => {
    const resp = await api.login({ username, password });
    tokens.set(resp);
    setAdmin(resp.admin);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // ignore — clear local anyway
    }
    tokens.clear();
    setAdmin(null);
    setStatus('anonymous');
  }, []);

  return (
    <Ctx.Provider value={{ status, admin, login, logout, refreshMe }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
